import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { JobPreferencesDropdown } from '@/components/planner/job-preferences-dropdown';
import {
  Calendar,
  Search,
  Filter,
  Star,
  Calendar as CalendarIcon,
  Users,
  Building,
  Briefcase,
  Clock,
  Settings,
  Link as LinkIcon
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import JobList from '@/components/dashboard/job-list';
import { Separator } from '@/components/ui/separator';

interface Job {
  id: string;
  title: string;
  skills: string[];
  matchPercentage?: number;
  employment_type: string;
  location: string;
  description?: string;
  department?: string;
  profiles?: {
    company_name?: string;
  };
}

interface InterviewPreferences {
  preferred_days: string[];
  preferred_times: string[];
  time_zone: string;
  notice_period: string;
  virtual_preferred: boolean;
}

export default function LoopsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [jobOpportunities, setJobOpportunities] = useState<Job[]>([]);
  const [matchedJobs, setMatchedJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [scheduledInterviews, setScheduledInterviews] = useState<any[]>([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState<InterviewPreferences>({
    preferred_days: [],
    preferred_times: [],
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    notice_period: '1_day',
    virtual_preferred: true
  });

  useEffect(() => {
    if (user) {
      fetchJobOpportunities();
      fetchScheduledInterviews();
      fetchInterviewPreferences();
    }
  }, [user]);

  const fetchJobOpportunities = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch all active jobs
      const { data, error } = await supabase
        .from('recruiting_projects')
        .select(`
          *,
          profiles(company_name)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      const jobs = data || [];
      setJobOpportunities(jobs);

      // Set all jobs as matched for now
      setMatchedJobs(jobs);
    } catch (error) {
      console.error('Error fetching job opportunities:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load job opportunities',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledInterviews = async () => {
    if (!user) return;
    
    setLoadingInterviews(true);
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select(`
          *,
          job:recruiting_projects(
            id,
            title,
            profiles(company_name)
          )
        `)
        .or(`participants.cs.{${user.email}},profile_id.eq.${user.id}`)
        .order('date', { ascending: true })
        .order('time', { ascending: true });
        
      if (error) throw error;
      setScheduledInterviews(data || []);
    } catch (error) {
      console.error('Error fetching scheduled interviews:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load scheduled interviews'
      });
    } finally {
      setLoadingInterviews(false);
    }
  };

  const fetchInterviewPreferences = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('interview_preferences')
        .eq('id', user.id)
        .single();
        
      if (error) throw error;
      
      if (data?.interview_preferences) {
        setPreferences(data.interview_preferences);
      }
    } catch (error) {
      console.error('Error fetching interview preferences:', error);
    }
  };

  const saveInterviewPreferences = async (newPreferences: InterviewPreferences) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ interview_preferences: newPreferences })
        .eq('id', user.id);
        
      if (error) throw error;
      
      setPreferences(newPreferences);
      toast({
        title: 'Success',
        description: 'Interview preferences saved successfully'
      });
      setPreferencesOpen(false);
    } catch (error) {
      console.error('Error saving interview preferences:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save interview preferences'
      });
    }
  };

  // Filter jobs based on selected filters
  const getFilteredJobs = (jobs: Job[]) => {
    return jobs.filter(job => {
      // First filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const titleMatch = job.title?.toLowerCase().includes(query);
        const companyMatch = job.profiles?.company_name?.toLowerCase().includes(query);
        const descriptionMatch = job.description?.toLowerCase().includes(query);
        const departmentMatch = job.department?.toLowerCase().includes(query);
        
        if (!titleMatch && !companyMatch && !descriptionMatch && !departmentMatch) {
          return false;
        }
      }
      
      // Filter by job type
      if (jobTypeFilter !== 'all' && job.employment_type !== jobTypeFilter) {
        return false;
      }
      
      // Filter by location
      if (locationFilter !== 'all' && job.location.toLowerCase().indexOf(locationFilter.toLowerCase()) === -1) {
        return false;
      }
      
      return true;
    });
  };

  // Get unique locations from jobs
  const uniqueLocations = Array.from(new Set(jobOpportunities.map(job => job.location)));

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        {/* Job Preferences Component */}
        <JobPreferencesDropdown />

        {/* Rest of the Loops page content remains the same */}
        <div className="flex flex-col space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Loops</h1>
            <p className="text-muted-foreground">
              Manage your interview loops and discover job opportunities that match your skills.
            </p>
          </div>

          {/* Scheduled Interviews Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-xl">Upcoming Interview Loops</CardTitle>
                <CardDescription>
                  Your scheduled and planned interviews
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                className="gap-1"
                onClick={() => setPreferencesOpen(true)}
              >
                <Settings className="h-4 w-4" />
                Interview Preferences
              </Button>
            </CardHeader>
            <CardContent>
              {loadingInterviews ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                </div>
              ) : scheduledInterviews.length > 0 ? (
                <div className="space-y-4">
                  {scheduledInterviews.map((interview) => (
                    <Card key={interview.id} className="bg-muted/30">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{interview.title}</h3>
                              <Badge variant={interview.status === 'confirmed' ? 'default' : 'secondary'}>
                                {interview.status === 'confirmed' ? 'Confirmed' : 'Pending Confirmation'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {interview.job?.title} at {interview.job?.profiles?.company_name}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1 text-sm">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                {interview.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                {interview.time}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                {interview.type}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="primary" 
                              size="sm"
                              asChild
                            >
                              <Link to={`/interviews/${interview.id}`}>
                                <LinkIcon className="h-4 w-4 mr-2" />
                                View Details
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <h3 className="text-lg font-medium mb-1">No Upcoming Interviews</h3>
                  <p className="text-muted-foreground">
                    You don't have any scheduled interviews yet. Apply to open positions to start getting interviews.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Interview Preferences Dialog */}
          <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Interview Preferences</DialogTitle>
                <DialogDescription>
                  Set your preferences for interview scheduling. Recruiters will try to accommodate these when scheduling interviews.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <label>Preferred Days</label>
                  <div className="flex flex-wrap gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day) => (
                      <Button
                        key={day}
                        variant={preferences.preferred_days.includes(day) ? 'default' : 'outline'}
                        onClick={() => {
                          const newDays = preferences.preferred_days.includes(day)
                            ? preferences.preferred_days.filter(d => d !== day)
                            : [...preferences.preferred_days, day];
                          setPreferences({ ...preferences, preferred_days: newDays });
                        }}
                        className="h-8"
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <label>Preferred Times</label>
                  <div className="flex flex-wrap gap-2">
                    {['Morning', 'Afternoon', 'Evening'].map((time) => (
                      <Button
                        key={time}
                        variant={preferences.preferred_times.includes(time) ? 'default' : 'outline'}
                        onClick={() => {
                          const newTimes = preferences.preferred_times.includes(time)
                            ? preferences.preferred_times.filter(t => t !== time)
                            : [...preferences.preferred_times, time];
                          setPreferences({ ...preferences, preferred_times: newTimes });
                        }}
                        className="h-8"
                      >
                        {time}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <label>Time Zone</label>
                  <Select
                    value={preferences.time_zone}
                    onValueChange={(value) => setPreferences({ ...preferences, time_zone: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {Intl.supportedValuesOf('timeZone').map((zone) => (
                        <SelectItem key={zone} value={zone}>
                          {zone}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid gap-2">
                  <label>Minimum Notice Period</label>
                  <Select
                    value={preferences.notice_period}
                    onValueChange={(value) => setPreferences({ ...preferences, notice_period: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select notice period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1_day">1 Day</SelectItem>
                      <SelectItem value="2_days">2 Days</SelectItem>
                      <SelectItem value="3_days">3 Days</SelectItem>
                      <SelectItem value="1_week">1 Week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="virtual"
                    checked={preferences.virtual_preferred}
                    onChange={(e) => setPreferences({ ...preferences, virtual_preferred: e.target.checked })}
                  />
                  <label htmlFor="virtual">Prefer virtual interviews when possible</label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPreferencesOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => saveInterviewPreferences(preferences)}>
                  Save Preferences
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Job Opportunities Section */}
          <Card>
            <CardHeader>
              <CardTitle>Job Opportunities</CardTitle>
              <CardDescription>
                Discover job opportunities that match your skills and experience.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Job Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search job titles, companies, or keywords..." 
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 flex-wrap md:flex-nowrap">
                  <div className="w-full md:w-auto">
                    <Select value={locationFilter} onValueChange={setLocationFilter}>
                      <SelectTrigger className="w-full md:w-[180px]">
                        <SelectValue placeholder="All Locations" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        {uniqueLocations.map((location) => (
                          <SelectItem key={location} value={location}>{location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-full md:w-auto">
                    <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                      <SelectTrigger className="w-full md:w-[180px]">
                        <SelectValue placeholder="All Job Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Job Types</SelectItem>
                        <SelectItem value="full-time">Full-time</SelectItem>
                        <SelectItem value="part-time">Part-time</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="internship">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button variant="ghost" size="icon" onClick={() => {
                    setSearchQuery('');
                    setLocationFilter('all');
                    setJobTypeFilter('all');
                  }} className="shrink-0">
                    <Filter className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            
              <Tabs defaultValue="matches" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="matches" className="flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Top Matches
                  </TabsTrigger>
                  <TabsTrigger value="all" className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    All Jobs
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="matches" className="mt-4">
                  <JobList 
                    jobs={getFilteredJobs(matchedJobs)}
                    loading={loading}
                    showMatchPercentage={false}
                    emptyMessage="No matching jobs found. Update your skills to improve matches."
                  />
                </TabsContent>
                
                <TabsContent value="all" className="mt-4">
                  <JobList 
                    jobs={getFilteredJobs(jobOpportunities)}
                    loading={loading}
                    showMatchPercentage={false}
                    emptyMessage="No job opportunities available at this time."
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* How Loops Work Section */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle>How Loops Work</CardTitle>
              <CardDescription>
                A loop is our term for the interview process. Here's how it works:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-2">
                    <Star className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-medium mb-1">1. Match & Apply</h3>
                  <p className="text-sm text-muted-foreground">
                    Find jobs that match your skills and apply with your professional profile.
                  </p>
                </div>
                
                <div className="flex flex-col items-center text-center">
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-2">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-medium mb-1">2. Schedule Interviews</h3>
                  <p className="text-sm text-muted-foreground">
                    Coordinate with employers to schedule interviews at convenient times.
                  </p>
                </div>
                
                <div className="flex flex-col items-center text-center">
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-2">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-medium mb-1">3. Complete the Loop</h3>
                  <p className="text-sm text-muted-foreground">
                    Successfully navigate the interview process and receive job offers.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}